/* oxlint-disable no-console */
import { LogsGlobal } from '@datadog/browser-logs';
import { RumInitConfiguration, RumPublicApi } from '@datadog/browser-rum-core';

import { isBuild, isServer } from './env-helper';
import { removeLocalePathname } from './pathname';

let isInitDatadog = false;
let buildEnv: BaseOptions['env'];
let rumApi: RumPublicApi;
let logsApi: LogsGlobal;

interface BaseOptions {
  env: 'prod' | 'stg' | 'dev' | 'sandbox';
  service: 'prex-frontend-nextjs' | 'prex-webview';
  applicationId: string;
}

export function datadogInit({
  rum,
  logs,
  ...options
}: { rum: RumPublicApi; logs: LogsGlobal } & RumInitConfiguration & BaseOptions) {
  if (isInitDatadog || isServer) return;

  rumApi = rum;
  logsApi = logs;
  isInitDatadog = true;
  buildEnv = options.env;

  rumApi.init({
    site: 'datadoghq.com',
    sessionSampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [
      (urlString: string) => {
        const url = new URL(urlString);
        /**
         * https://prextech.slack.com/archives/C05BSR5LQUX/p1716976971212569
         * klines request should be simple request. when allow tracing, datadog injects custom header
         */
        if (url.pathname.startsWith('/api/v2/chart/klines/')) return false;
        return ['api.flipster.io', 'api.flipster.me', 'api.stg.prex.run'].includes(url.hostname);
      },
    ],
    ...options,
    beforeSend(event, context) {
      if (event.view && !event.view.name) {
        const url = new URL(event.view.url);
        // oxlint-disable-next-line no-param-reassign
        event.view.name = removeLocalePathname(url.pathname);
      }
      if (event.type === 'resource' && 'resource' in event) {
        const url = new URL(event.resource.url);
        return ['api.segment.io', 'in.ap1.segmentapis.com', 'www.google-analytics.com'].every(
          (blockedHost) => url.hostname !== blockedHost,
        );
      }
      return options.beforeSend?.(event, context) ?? true;
    },
  });

  logsApi.init({
    site: 'datadoghq.com',
    sessionSampleRate: 100,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    clientToken: options.clientToken,
    env: options.env,
    service: options.service,
    version: options.version,
  });
}

export const datadog = {
  setUser(user: { id: string; email: string; uid: string | undefined } | null) {
    if (!isInitDatadog) return;
    if (user) {
      rumApi.setUser(user);
      logsApi.setUser(user);
    } else {
      rumApi.clearUser();
      logsApi.clearUser();
    }
  },
  setContext(key: string, value: string | number | boolean) {
    if (!isInitDatadog) return;
    rumApi.setGlobalContextProperty(key, value);
    logsApi.setGlobalContextProperty(key, value);
  },
  setAppContext(context: Record<string, string | number | boolean>) {
    if (!isInitDatadog) return;
    if (Object.values(context).every((value) => !value)) return;
    rumApi.setGlobalContext(context);
    logsApi.setGlobalContext(context);
  },
  removeContext(key: string) {
    if (!isInitDatadog) return;
    rumApi.removeGlobalContextProperty(key);
    logsApi.removeGlobalContextProperty(key);
  },
  addAction(name: string, context: Record<string, string | number>) {
    if (!isInitDatadog) return;
    rumApi.addAction(name, context);
  },
  addTiming(name: string) {
    if (!isInitDatadog) return;
    rumApi.addTiming(name);
  },
  addError(error: unknown, context?: Record<string, unknown>) {
    if (!isInitDatadog) return;
    const normalizedError = normalizeLogError(error);
    rumApi.addError(normalizedError, context);
    logsApi.logger.error(normalizedError?.message ?? 'Unknown Error', context, normalizedError);
  },
};

function normalizeLogError(error: unknown) {
  if (error instanceof Error) return error;
  if (error != null) return new Error(String(error));
  return undefined;
}

function createLogger(name: string, context: Record<string, unknown>) {
  const shouldLogToConsole = () => {
    if (!isBuild) return true;
    return !isServer && ['stg', 'sandbox', 'dev'].includes(buildEnv);
  };

  return {
    info: (message: string, extraContext?: Record<string, unknown>) => {
      if (shouldLogToConsole()) {
        console.log(`[Datadog - ${name}]`, { message, ...extraContext });
      }
      if (!logsApi) return;
      const logger = logsApi.createLogger(name, { context });
      logger.info(message, extraContext);
    },
    warn: (message: string, extraContext?: Record<string, unknown>, error?: unknown) => {
      if (shouldLogToConsole()) {
        console.warn(`[Datadog - ${name}]`, { message, ...extraContext });
      }
      if (!logsApi) return;
      const logger = logsApi.createLogger(name, { context });
      logger.warn(message, extraContext, normalizeLogError(error));
    },
    error: (message: string, extraContext?: Record<string, unknown>, error?: unknown) => {
      if (shouldLogToConsole()) {
        console.error(`[Datadog - ${name}]`, { message, error, ...extraContext });
      }
      if (!logsApi || !rumApi) return;
      const logger = logsApi.createLogger(name, { context });
      logger.error(message, extraContext, normalizeLogError(error));
      rumApi.addError(normalizeLogError(error) ?? extraContext?.error_message ?? message, {
        ...context,
        message,
      });
    },
  };
}

export const tradeLogger = createLogger('trade', {
  domain: 'trade',
  log_version: 1,
});

export const websocketLogger = createLogger('websocket', {
  domain: 'websocket',
  log_version: 1,
});
